/**
 * WebGL2GpuTimer — per-pass GPU timing via EXT_disjoint_timer_query_webgl2
 * (falling back to the older EXT_disjoint_timer_query name — three.js's own
 * WebGLTimestampQueryPool accepts both on a WebGL2 context).
 *
 * Architecture (3-frame ring buffer to avoid stalls):
 *
 *  Frame N  : createQuery(GL_TIME_ELAPSED) per pass
 *             beginQuery / endQuery wrapping each pass
 *             Store queries in ring slot N % RING_SIZE
 *
 *  Frame N+3: poll QUERY_RESULT_AVAILABLE for each query in slot (N+3-3) % RING_SIZE
 *             If disjoint → discard (GPU clock was reset — unreliable data)
 *             If available → read QUERY_RESULT (nanoseconds, u64 in two u32s)
 *
 * All polling is done in endFrame() at the START of the frame processing.
 * This ensures we never stall: by frame N+3, frame N's results are almost
 * always available without waiting.
 *
 * IMPORTANT — API shape: on a WebGL2 context, EXT_disjoint_timer_query_webgl2
 * supplies only the TIME_ELAPSED_EXT / GPU_DISJOINT_EXT constants (plus a
 * counter-based queryCounterEXT path this backend does not use). The query
 * OBJECTS themselves — createQuery/beginQuery/endQuery/getQueryParameter/
 * deleteQuery — come from the CORE WebGL2RenderingContext, not the extension.
 * The extension does NOT define createQueryEXT/beginQueryEXT/endQueryEXT/
 * getQueryObjectEXT — those belong to the older, WebGL1-only
 * EXT_disjoint_timer_query extension's own query-object API and do not exist
 * here; calling them on this extension throws.
 *
 * Limitations:
 *  - Nested beginPass/endPass for the same GL_TIME_ELAPSED query is not
 *    supported by the extension — each pass must be non-overlapping.
 *  - maxPasses entries per frame (default 64). Passes beyond this are dropped.
 */
import type { IGpuTimerBackend } from './GpuTimerBackend.js';
import { Logger } from '../../core/Logger.js';

// Opaque WebGLQuery handle returned by gl.createQuery(); we only ever pass it
// back to gl.* methods, so a structural marker keeps it nominal without
// constraining its (browser-defined) shape.
type WebGLQueryLike = { readonly __webglQueryBrand: unique symbol } | object;

/**
 * Subset of WebGL2RenderingContext we need. The query-object API
 * (createQuery/beginQuery/endQuery/getQueryParameter/deleteQuery) is CORE
 * WebGL2 — it is not part of the timer-query extension.
 */
interface GL2Like {
  getExtension(name: string): unknown;
  getError(): number;
  getParameter(pname: number): unknown;
  createQuery(): WebGLQueryLike;
  deleteQuery(query: WebGLQueryLike): void;
  beginQuery(target: number, query: WebGLQueryLike): void;
  endQuery(target: number): void;
  getQueryParameter(query: WebGLQueryLike, pname: number): number | boolean;
  readonly QUERY_RESULT: number;
  readonly QUERY_RESULT_AVAILABLE: number;
}

/**
 * What EXT_disjoint_timer_query_webgl2 (or its EXT_disjoint_timer_query
 * fallback) actually provides on a WebGL2 context: two constants that drive
 * the CORE query API above. No query-object methods live here.
 */
interface EXTDisjointLike {
  readonly TIME_ELAPSED_EXT: number;
  readonly GPU_DISJOINT_EXT: number;
}

const RING_SIZE = 3; // frames to pipeline before reading back

interface FrameRecord {
  queries: WebGLQueryLike[];
  names: string[];
  count: number;
}

export class WebGL2GpuTimer implements IGpuTimerBackend {
  private readonly _gl: GL2Like;
  private readonly _ext: EXTDisjointLike | null;
  private readonly _maxPasses: number;
  private readonly _ring: FrameRecord[];
  private _writeIndex = 0;
  private _resolved: Map<string, number> = new Map();
  private _isAvailable: boolean;

  /**
   * @param gl        The WebGL2RenderingContext (typed as unknown for compatibility).
   * @param maxPasses Maximum number of pass measurements per frame (default 64).
   */
  constructor(gl: unknown, maxPasses = 64) {
    this._gl = gl as GL2Like;
    this._maxPasses = maxPasses;

    // Either extension name enables timer queries on a WebGL2 context; prefer
    // the WebGL2-suffixed one and fall back to the older name so timing is
    // still available on hosts that only expose EXT_disjoint_timer_query.
    const ext =
      (this._gl.getExtension('EXT_disjoint_timer_query_webgl2') as EXTDisjointLike | null) ??
      (this._gl.getExtension('EXT_disjoint_timer_query') as EXTDisjointLike | null);
    this._ext = ext ?? null;
    this._isAvailable = this._ext !== null;

    // Pre-allocate ring records (queries allocated on demand per frame)
    this._ring = Array.from({ length: RING_SIZE }, (): FrameRecord => ({
      queries: [],
      names: [],
      count: 0,
    }));

    if (this._isAvailable) {
      Logger.info('WebGL2GpuTimer', `Initialized — maxPasses=${maxPasses}, ring size=${RING_SIZE}`);
    } else {
      Logger.info('WebGL2GpuTimer', 'EXT_disjoint_timer_query_webgl2 not available — GPU timing disabled');
    }
  }

  get available(): boolean {
    return this._isAvailable;
  }

  /** True while a TIME_ELAPSED query is open (beginPass without its endPass). */
  private _queryOpen = false;

  beginPass(name: string): void {
    if (!this._isAvailable || !this._ext) return;
    const slot = this._ring[this._writeIndex % RING_SIZE];
    if (slot.count >= this._maxPasses) return;

    // Query objects come from the CORE WebGL2 API — the extension only
    // supplies the TIME_ELAPSED_EXT target constant.
    const query = this._gl.createQuery();
    this._gl.beginQuery(this._ext.TIME_ELAPSED_EXT, query);
    this._queryOpen = true;

    if (slot.count >= slot.queries.length) {
      slot.queries.push(query);
      slot.names.push(name);
    } else {
      slot.queries[slot.count] = query;
      slot.names[slot.count] = name;
    }
    slot.count++;
  }

  endPass(_name: string): void {
    if (!this._isAvailable || !this._ext) return;
    // WHY: beginPass early-returns once the slot is full, but endPass still
    // issued endQuery — GL_INVALID_OPERATION every frame past maxPasses.
    if (!this._queryOpen) return;
    this._queryOpen = false;
    this._gl.endQuery(this._ext.TIME_ELAPSED_EXT);
  }

  endFrame(): void {
    if (!this._isAvailable || !this._ext) return;
    const ext = this._ext;
    const gl = this._gl;

    // --- Poll oldest slot (RING_SIZE frames ago) ---
    // After 3 frames the results are almost certainly ready
    const readIndex = (this._writeIndex + 1) % RING_SIZE;
    const readSlot = this._ring[readIndex];

    if (readSlot.count > 0) {
      // Check disjoint flag first — if set, GPU timing was interrupted.
      // GPU_DISJOINT_EXT is a context-level parameter read via
      // `gl.getParameter(pname)`, NOT a per-query value read via
      // `getQueryParameter`. Passing a fake empty-object "query" would
      // return a meaningless value and never detect a real disjoint event.
      const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
      const isDisjoint = disjoint === true || disjoint === 1;

      const result = new Map<string, number>();
      for (let i = 0; i < readSlot.count; i++) {
        const query = readSlot.queries[i];
        const name = readSlot.names[i];
        if (!query || !name) continue;

        if (!isDisjoint) {
          const available = gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE);
          if (available === true || available === 1) {
            // QUERY_RESULT returns nanoseconds as a u64; in WebGL2,
            // getQueryParameter returns a JS number (doubles cover
            // ~9007e12 ns ~ 9007s exactly).
            const ns = gl.getQueryParameter(query, gl.QUERY_RESULT) as number;
            if (ns > 0) {
              result.set(name, ns);
            }
          }
        }

        gl.deleteQuery(query);
      }

      if (!isDisjoint) {
        this._resolved = result;
      }

      // Reset the slot for reuse
      readSlot.queries.length = 0;
      readSlot.names.length = 0;
      readSlot.count = 0;
    }

    // --- Advance write pointer to next slot ---
    this._writeIndex++;
    const nextWrite = this._writeIndex % RING_SIZE;
    const nextSlot = this._ring[nextWrite];

    // If there are leftover queries in the next write slot (shouldn't happen with ring size 3),
    // clean them up
    for (let i = 0; i < nextSlot.count; i++) {
      const q = nextSlot.queries[i];
      if (q) gl.deleteQuery(q);
    }
    nextSlot.queries.length = 0;
    nextSlot.names.length = 0;
    nextSlot.count = 0;
  }

  readFrame(): ReadonlyMap<string, number> {
    return this._resolved;
  }

  dispose(): void {
    if (!this._isAvailable || !this._ext) return;
    const gl = this._gl;
    for (const slot of this._ring) {
      for (let i = 0; i < slot.count; i++) {
        const q = slot.queries[i];
        if (q) gl.deleteQuery(q);
      }
      slot.queries.length = 0;
      slot.names.length = 0;
      slot.count = 0;
    }
    this._resolved.clear();
  }
}
